// Mind Map HTML Generator for TweePorter.ca
export const generateMindMapHtml = (twee, styleColors = {}) => {
  const { 
    boldColor = '#fbd38d', 
    italicColor = '#b794f4', 
    headingColor = '#90cdf4', 
    linkColor = '#667eea',
    textBgColor = '#ffffff',
    frameBgColor = '#f8f9fa'
  } = styleColors;
  
  const passages = parseTwee(twee);
  const storyTitle = passages.find(p => p.name === 'StoryTitle')?.content.trim() || 'Interactive Story';
  const storyPassages = passages.filter(p => p.name !== 'StoryTitle');
  
  // Find start passage
  let startPassage = storyPassages.find(p => p.name === 'Start') || storyPassages[0];
  
  if (!startPassage) {
    throw new Error('No passages found');
  }

  // Helper function to process content (same as regular compiler)
  const processContent = (text) => {
    let processed = text;
    
    // Remove link syntax completely (don't leave empty bullets)
    processed = processed.replace(/\[\[([^\]]+)\]\]/g, '');
    
    // Remove lines that are just bullets with no content (but keep truly empty lines)
    processed = processed.split('\n').filter(line => {
      const trimmed = line.trim();
      // Keep empty lines for spacing, but remove lines that are just bullet markers
      return trimmed !== '*' && trimmed !== '-';
    }).join('\n');
    
    // SugarCube heading: !Heading
    processed = processed.replace(/^!{1,6}\s*(.+)$/gm, (match, text) => {
      const level = match.match(/^!+/)[0].length;
      return `<h${Math.min(level + 2, 6)}>${text}</h${Math.min(level + 2, 6)}>`;
    });
    
    // Markdown heading: # Heading
    processed = processed.replace(/^#{1,6}\s+(.+)$/gm, (match, text) => {
      const level = match.match(/^#+/)[0].length;
      return `<h${Math.min(level + 2, 6)}>${text}</h${Math.min(level + 2, 6)}>`;
    });
    
    // Bold
    processed = processed.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    processed = processed.replace(/''([^']+)''/g, '<strong>$1</strong>');
    
    // Italic (only use // syntax, not single asterisks to avoid conflicts)
    processed = processed.replace(/\/\/([^/]+)\/\//g, '<em>$1</em>');
    
    // Strikethrough
    processed = processed.replace(/~~([^~]+)~~/g, '<del>$1</del>');
    processed = processed.replace(/==([^=]+)==/g, '<del>$1</del>');
    
    // Underline
    processed = processed.replace(/__([^_]+)__/g, '<u>$1</u>');
    
    // Horizontal rule
    processed = processed.replace(/^---$/gm, '<hr>');
    
    // Bullets
    const lines = processed.split('\n');
    let inList = false;
    processed = lines.map(line => {
      const bulletMatch = line.match(/^\s*[\*\-]\s+(.+)$/);
      if (bulletMatch) {
        if (!inList) {
          inList = true;
          return '<ul>\n<li>' + bulletMatch[1] + '</li>';
        }
        return '<li>' + bulletMatch[1] + '</li>';
      } else {
        if (inList && line.trim() === '') {
          inList = false;
          return '</ul>\n';
        }
        return line;
      }
    }).join('\n');
    if (inList) processed += '</ul>';
    
    // Line breaks
    processed = processed.replace(/\n/g, '<br>');
    
    return processed;
  };

  // Extract links from passage
  const extractLinks = (content) => {
    const links = [];
    const linkRegex = /\[\[([^\]]+)\]\]/g;
    let match;
    
    while ((match = linkRegex.exec(content)) !== null) {
      const linkText = match[1];
      let label, target;
      
      if (linkText.includes('|')) {
        const parts = linkText.split('|');
        label = parts[0].trim();
        target = parts[1].trim();
      } else if (linkText.includes('->')) {
        const parts = linkText.split('->');
        label = parts[0].trim();
        target = parts[1].trim();
      } else {
        label = linkText.trim();
        target = linkText.trim();
      }
      
      links.push({ label, target: target.replace(/\s+/g, '-') });
    }
    
    return links;
  };

  // Build passages data
  const passagesData = storyPassages.map(passage => {
    const passageId = passage.name.replace(/\s+/g, '-');
    const content = processContent(passage.content);
    const links = extractLinks(passage.content);
    const isStart = passage.name === startPassage.name;
    
    return {
      id: passageId,
      name: passage.name,
      content: content,
      links: links,
      isStart: isStart
    };
  });

  // Generate HTML
  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${storyTitle} - Mind Map</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
            background: #1a202c;
            color: #e2e8f0;
            overflow-x: auto;
            overflow-y: auto;
            min-height: 100vh;
            padding: 40px 20px;
        }
        
        #mind-map {
            width: 100%;
            max-width: 100%;
            padding: 20px;
        }
        
        /* Horizontal layout */
        .node-container {
            display: flex;
            align-items: flex-start;
            gap: 30px;
            margin-bottom: 40px;
            animation: fadeIn 0.5s ease-in;
            position: relative;
        }
        
        @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        
        /* Breadcrumb bubbles - stacked above current node */
        .breadcrumb-trail {
            position: absolute;
            top: 0;
            left: 0;
            display: flex;
            flex-direction: column;
            gap: 5px;
            z-index: 10;
        }
        
        .bubble {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 10px 18px;
            border-radius: 25px;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
            cursor: pointer;
            transition: all 0.3s;
            white-space: nowrap;
            font-weight: 600;
            font-size: 10px;
            position: relative;
            min-width: fit-content;
        }
        
        .bubble:hover {
            transform: scale(1.05);
            box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
        }
        
        .bubble.current {
            background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
            box-shadow: 0 4px 15px rgba(72, 187, 120, 0.4);
            font-size: 16px;
            padding: 18px 30px;
            white-space: normal;
            max-width: 300px;
        }
        
        /* Connector lines */
        .connector {
            position: relative;
            width: 40px;
            height: 2px;
            background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
            align-self: center;
        }
        
        .connector::before {
            content: '';
            position: absolute;
            right: -6px;
            top: -4px;
            width: 0;
            height: 0;
            border-left: 8px solid #764ba2;
            border-top: 5px solid transparent;
            border-bottom: 5px solid transparent;
        }
        
        /* Current node section */
        .current-node-section {
            flex: 1;
            min-width: 250px;
            max-width: min(700px, 60vw);
            position: relative;
        }
        
        .node-header {
            display: flex;
            align-items: flex-start;
            gap: 15px;
            margin-bottom: 10px;
        }
        
        .current-bubble-wrapper {
            position: relative;
            flex: 1;
        }
        
        .toggle-btn {
            background: rgba(255, 255, 255, 0.1);
            border: 2px solid rgba(255, 255, 255, 0.3);
            color: white;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            cursor: pointer;
            font-size: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s;
        }
        
        .toggle-btn:hover {
            background: rgba(255, 255, 255, 0.2);
            transform: rotate(180deg);
        }
        
        .toggle-btn.collapsed {
            transform: rotate(0deg);
        }
        
        .toggle-btn.collapsed:hover {
            transform: rotate(0deg) scale(1.1);
        }
        
        /* Content area */
        .node-content {
            background: ${textBgColor};
            border-radius: 12px;
            padding: 25px;
            margin-top: 15px;
            line-height: 1.8;
            animation: fadeIn 0.3s ease-in;
            border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .node-content.hidden {
            display: none;
        }
        
        .node-content h3, .node-content h4 {
            color: ${headingColor};
            margin: 15px 0 10px 0;
        }
        
        .node-content strong {
            color: ${boldColor};
        }
        
        .node-content em {
            color: ${italicColor};
        }
        
        .node-content ul {
            margin: 10px 0;
            padding-left: 25px;
        }
        
        .node-content li {
            margin: 2px 0;
            line-height: 1.4;
        }
        
        .node-content hr {
            border: none;
            border-top: 1px solid rgba(255, 255, 255, 0.2);
            margin: 20px 0;
        }
        
        /* Next options */
        .next-options {
            display: flex;
            flex-direction: column;
            gap: 15px;
            align-items: flex-start;
            position: relative;
            align-self: center;
            margin-top: 0;
        }
        
        .next-options.hidden {
            display: none;
        }
        
        .option-wrapper {
            display: flex;
            align-items: center;
            gap: 0;
        }
        
        .option-line {
            width: 30px;
            height: 2px;
            background: linear-gradient(90deg, #48bb78 0%, #4299e1 100%);
            position: relative;
        }
        
        .option-line::after {
            content: '';
            position: absolute;
            right: -5px;
            top: -3px;
            width: 0;
            height: 0;
            border-left: 6px solid #4299e1;
            border-top: 4px solid transparent;
            border-bottom: 4px solid transparent;
        }
        
        .option-bubble {
            background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
            color: white;
            padding: 12px 20px;
            border-radius: 25px;
            cursor: pointer;
            transition: all 0.3s;
            white-space: nowrap;
            font-size: 13px;
            box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
        }
        
        .option-bubble:hover {
            transform: translateX(5px);
            box-shadow: 0 4px 15px rgba(66, 153, 225, 0.4);
        }
        
        /* Layout toggle button */
        .layout-toggle {
            background: rgba(255, 255, 255, 0.1);
            border: 2px solid rgba(255, 255, 255, 0.3);
            color: white;
            padding: 8px 16px;
            border-radius: 20px;
            cursor: pointer;
            font-size: 11px;
            transition: all 0.3s;
            margin-bottom: 10px;
            display: inline-block;
        }
        
        .layout-toggle:hover {
            background: rgba(255, 255, 255, 0.2);
            transform: scale(1.05);
        }
        
        /* Options at bottom layout */
        .next-options.at-bottom {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            background: rgba(45, 55, 72, 0.95);
            backdrop-filter: blur(10px);
            padding: 20px;
            margin: 0;
            flex-direction: row;
            justify-content: center;
            align-items: center;
            gap: 20px;
            box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
            z-index: 50;
        }
        
        .next-options.at-bottom .option-line {
            display: none;
        }
        
        .next-options.at-bottom .option-wrapper {
            gap: 0;
        }
        
        /* Mobile responsive */
        @media (max-width: 768px) {
            body {
                padding: 20px 10px;
            }
            
            .node-container {
                gap: 15px;
            }
            
            .breadcrumb-trail {
                gap: 5px;
            }
            
            .bubble {
                font-size: 9px;
                padding: 8px 14px;
            }
            
            .bubble.current {
                font-size: 14px;
                padding: 15px 22px;
            }
            
            .current-node-section {
                min-width: 200px;
                max-width: 100%;
            }
            
            #mind-map {
                padding: 20px 5px;
            }
            
            .node-content {
                padding: 20px;
                font-size: 14px;
            }
            
            .option-bubble {
                font-size: 12px;
                padding: 10px 16px;
            }
        }
    </style>
</head>
<body>
    <div id="mind-map">
        <!-- Content will be dynamically generated here -->
    </div>

    <script>
        // Passages data
        const passages = ${JSON.stringify(passagesData)};
        
        // State
        let history = [];
        let currentIndex = -1;
        let contentVisible = false;
        let optionsAtBottom = false;
        
        // Toggle layout
        function toggleLayout() {
            optionsAtBottom = !optionsAtBottom;
            const btn = document.getElementById('layout-toggle');
            btn.textContent = optionsAtBottom ? 'Options: Bottom ↓' : 'Options: Side →';
            
            // Re-render current passage
            const passage = passages.find(p => p.id === history[currentIndex]);
            if (passage) {
                renderMindMap(passage);
            }
        }
        
        // Make toggleLayout global
        window.toggleLayout = toggleLayout;
        
        // Initialize
        function init() {
            const startPassage = passages.find(p => p.isStart) || passages[0];
            // Initialize history with start passage
            currentIndex = 0;
            history = [startPassage.id];
            // Render the initial view
            renderMindMap(startPassage);
        }
        
        // Navigate to passage
        function navigateToPassage(passageId, addToHistory = true) {
            const passage = passages.find(p => p.id === passageId);
            if (!passage) return;
            
            // Update history
            if (addToHistory) {
                currentIndex++;
                history = history.slice(0, currentIndex);
                history.push(passageId);
            }
            
            // Render mind map
            renderMindMap(passage);
            
            // Scroll to top
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        
        // Render mind map
        function renderMindMap(currentPassage) {
            const container = document.getElementById('mind-map');
            
            // Build breadcrumb trail - stacked vertically with oldest at top
            let breadcrumbHtml = '<div class="breadcrumb-trail">';
            
            // Only show previous nodes (not current) stacked from oldest to newest
            for (let i = 0; i < currentIndex; i++) {
                const passageId = history[i];
                const p = passages.find(p => p.id === passageId);
                
                breadcrumbHtml += \`
                    <div class="bubble" onclick="jumpToHistory(\${i})">
                        \${p.name}
                    </div>
                \`;
            }
            breadcrumbHtml += '</div>';
            
            // Build current node section
            let nodeHtml = \`
                <div class="current-node-section">
                    <button class="layout-toggle" id="layout-toggle" onclick="window.toggleLayout()">
                        \${optionsAtBottom ? 'Options: Bottom ↓' : 'Options: Side →'}
                    </button>
                    <div class="node-header">
                        <button class="toggle-btn \${contentVisible ? '' : 'collapsed'}" id="toggle-button" onclick="window.toggleContent()">
                            \${contentVisible ? '˅' : '>'}
                        </button>
                        <h2 style="color: #90cdf4; font-size: 20px;">\${currentPassage.name}</h2>
                    </div>
                    <div class="node-content \${contentVisible ? '' : 'hidden'}">
                        \${currentPassage.content}
                    </div>
                </div>
            \`;
            
            // Build next options (only show when content is visible)
            let optionsHtml = \`<div class="next-options \${contentVisible ? '' : 'hidden'} \${optionsAtBottom ? 'at-bottom' : ''}">\`;
            currentPassage.links.forEach(link => {
                optionsHtml += \`
                    <div class="option-wrapper">
                        <div class="option-line"></div>
                        <div class="option-bubble" onclick="navigateToPassage('\${link.target}', true)">
                            \${link.label}
                        </div>
                    </div>
                \`;
            });
            optionsHtml += '</div>';
            
            // Calculate dynamic padding based on number of breadcrumbs
            const breadcrumbCount = currentIndex;
            const breadcrumbHeight = 35; // approximate height per breadcrumb (padding + font + gap)
            const dynamicPadding = breadcrumbCount > 0 ? (breadcrumbCount * breadcrumbHeight) + 20 : 20;
            
            // Combine all
            container.innerHTML = \`
                <div class="node-container" style="padding-top: \${dynamicPadding}px;">
                    \${breadcrumbHtml}
                    \${nodeHtml}
                    \${optionsHtml}
                </div>
            \`;
        }
        
        // Toggle content visibility
        function toggleContent() {
            try {
                contentVisible = !contentVisible;
                console.log('Toggle clicked, contentVisible now:', contentVisible);
                console.log('Current index:', currentIndex);
                console.log('History:', history);
                console.log('Passages available:', passages.length);
                
                const passageId = history[currentIndex];
                console.log('Looking for passage with id:', passageId);
                
                const passage = passages.find(p => p.id === passageId);
                console.log('Found passage:', passage);
                
                if (passage) {
                    renderMindMap(passage);
                } else {
                    console.error('No passage found for currentIndex:', currentIndex, 'passageId:', passageId);
                }
            } catch (error) {
                console.error('Error in toggleContent:', error);
            }
        }
        
        // Expose toggleContent to global scope for inline onclick
        window.toggleContent = toggleContent;
        
        // Jump to history point
        function jumpToHistory(index) {
            currentIndex = index;
            const passageId = history[currentIndex];
            const passage = passages.find(p => p.id === passageId);
            renderMindMap(passage);
        }
        
        // Start
        init();
    </script>
</body>
</html>`;
};

// Helper function to parse Twee (copied from main compiler)
function parseTwee(twee) {
  const passages = [];
  const lines = twee.split('\n');
  let currentPassage = null;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    if (line.startsWith('::')) {
      if (currentPassage) {
        passages.push(currentPassage);
      }
      
      const name = line.substring(2).trim();
      currentPassage = {
        name: name,
        content: ''
      };
    } else if (currentPassage) {
      currentPassage.content += line + '\n';
    }
  }
  
  if (currentPassage) {
    passages.push(currentPassage);
  }
  
  passages.forEach(p => {
    p.content = p.content.trim();
  });
  
  return passages;
}
